/* eslint-disable react/prop-types */
/* eslint-disable react/jsx-key */

// see: https://react-table-v7-docs.netlify.app/docs/api/useTable#column-options
import {
    Table as ChakraTable,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    TableContainer,
    Text,
    forwardRef,
    HStack,
    IconButton,
    ButtonGroup,
    Box,
} from '@chakra-ui/react'
import PropTypes from 'prop-types'
import { createContext, useContext } from 'react'
import {
    useGlobalFilter,
    usePagination,
    useSortBy,
    useTable,
} from 'react-table'

export * from './Columns.jsx'

// https://react-table-v7-docs.netlify.app/docs/examples/data-driven-classes-and-styles
const defaultPropGetter = () => ({})

/** @returns {any} */
export const useReactTable = (options, ...hooks) => {
    return useTable(
        {
            defaultColumn: {
                width: 'auto',
            },
            disableSortRemove: true,
            // これでソートを無効化できる
            // disableSortBy: true,
            ...options,
        },
        useSortBy,
        ...hooks
    )
}

/** @returns {any} */
export const useServerSide = (options, ...hooks) => {
    return useTable(
        {
            defaultColumn: {
                width: 'auto',
            },
            disableSortRemove: true,
            manualSortBy: true,
            manualPagination: true,
            manualGlobalFilter: true,
            ...options,
        },
        useGlobalFilter,
        useSortBy,
        usePagination,
        ...hooks
    )
}

const TableContext = createContext(null)
export const useTableContext = () => {
    return useContext(TableContext)
}

const NoDataBoundary = ({ children }) => {
    const { rows } = useTableContext()
    if (rows.length) {
        return children
    }
    // TODO: 設定可能にしたい
    return <Text className="no-data-text">データが存在しません</Text>
}
NoDataBoundary.propTypes = {
    children: PropTypes.node.isRequired,
}

export const Provider = ({ children, ...data }) => {
    // console.log(data)
    return (
        <TableContext.Provider value={data}>
            <TableContainer>
                <NoDataBoundary>{children}</NoDataBoundary>
            </TableContainer>
        </TableContext.Provider>
    )
}
Provider.propTypes = {
    children: PropTypes.node.isRequired,
}

export const Table = forwardRef((props, ref) => {
    const { getTableProps } = useTableContext()
    return (
        <ChakraTable
            ref={ref}
            {...getTableProps()}
            layerStyle="table-bordered"
            {...props}
        />
    )
})

export const Head = forwardRef((props, ref) => {
    const { headerGroups } = useTableContext()
    return (
        <Thead ref={ref} bg="tryt.gray-4" {...props}>
            {headerGroups.map((headerGroup) => {
                return (
                    <Tr {...headerGroup.getHeaderGroupProps()}>
                        {headerGroup.headers.map(
                            /**
                             * @param {import('react-table').HeaderGroup &
                             *     import('react-table').UseSortByColumnProps} column
                             */
                            (column) => {
                                return (
                                    <Th
                                        width={column['width']}
                                        {...column.getHeaderProps([
                                            {
                                                className: column['className'],
                                            },
                                            column.getSortByToggleProps(),
                                        ])}
                                    >
                                        {column.render('Header')}
                                        {/* see: https://react-table-v7-docs.netlify.app/docs/examples/sorting */}
                                        {/* Add a sort direction indicator */}
                                        <Text as="span">
                                            {column.isSorted
                                                ? column.isSortedDesc
                                                    ? ' ▼'
                                                    : ' ▲'
                                                : ''}
                                        </Text>
                                    </Th>
                                )
                            }
                        )}
                    </Tr>
                )
            })}
        </Thead>
    )
})

export const Body = forwardRef(
    (
        {
            getRowProps = defaultPropGetter,
            // getColumnProps = defaultPropGetter,
            // getCellProps = defaultPropGetter,
            ...props
        },
        ref
    ) => {
        const { prepareRow, rows } = useTableContext()
        return (
            <Tbody ref={ref} {...props}>
                {rows.map((row) => {
                    prepareRow(row)
                    return (
                        // see: https://react-table-v7-docs.netlify.app/docs/examples/data-driven-classes-and-styles
                        <Tr {...row.getRowProps(getRowProps(row))}>
                            {row.cells.map((cell) => {
                                return (
                                    <Td
                                        {...cell.getCellProps([
                                            {
                                                className:
                                                    cell.column['className'],
                                            },
                                            // getColumnProps(cell.column),
                                            // getCellProps(cell),
                                        ])}
                                    >
                                        {cell.render('Cell')}
                                    </Td>
                                )
                            })}
                        </Tr>
                    )
                })}
            </Tbody>
        )
    }
)

export const Pagination = forwardRef((props, ref) => {
    // see: https://react-table-v7-docs.netlify.app/docs/examples/pagination
    const {
        pageCount,
        canPreviousPage,
        canNextPage,
        previousPage,
        nextPage,
        state: { pageIndex },
    } = useTableContext()
    return (
        <HStack ref={ref} mt={6} spacing={4} {...props}>
            <ButtonGroup size="sm">
                <IconButton
                    isDisabled={!canPreviousPage}
                    onClick={previousPage}
                    aria-label="Previous Page"
                    height="28px"
                    minW="28px"
                    icon={
                        <Box as="span" fontSize="md" fontWeight="bold" pr="1px">
                            ＜
                        </Box>
                    }
                />
                <IconButton
                    isDisabled={!canNextPage}
                    onClick={nextPage}
                    aria-label="Next Page"
                    height="28px"
                    minW="28px"
                    icon={
                        <Box as="span" fontSize="md" fontWeight="bold" pl="1px">
                            ＞
                        </Box>
                    }
                />
            </ButtonGroup>
            <Text>
                Page {pageIndex + 1} / {pageCount}
            </Text>
        </HStack>
    )
})
